import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const CookiePolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-2xl mb-4">
              <Cookie className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">Cookie Policy</h1>
            <p className="text-slate-600 dark:text-slate-400">Last updated: January 2026</p>
          </div>

          {/* Content */}
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 sm:p-8 space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">What Are Cookies?</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  Cookies are small text files that are placed on your device when you visit our website. They help us provide 
                  you with a better experience by remembering your preferences, enabling certain functionality, and helping us 
                  understand how you use our service.
                </p>
              </section>

              {/* Types of Cookies */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Types of Cookies We Use</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-600 dark:border-blue-400 pl-4">
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">1. Necessary Cookies (Always Active)</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-2">
                      These cookies are essential for the website to function properly and cannot be disabled.
                    </p>
                    <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-sm">
                      <li>• Authentication cookies (session_token)</li>
                      <li>• Security cookies (CSRF tokens)</li>
                      <li>• Load balancing cookies</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-600 dark:border-green-400 pl-4">
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">2. Functional Cookies</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-2">
                      These cookies enable enhanced functionality and personalization.
                    </p>
                    <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-sm">
                      <li>• Language preferences</li>
                      <li>• Theme settings (dark mode)</li>
                      <li>• User interface preferences</li>
                      <li>• Recently viewed items</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-yellow-600 dark:border-yellow-400 pl-4">
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">3. Analytics Cookies</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-2">
                      These cookies help us understand how visitors interact with our website.
                    </p>
                    <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-sm">
                      <li>• Google Analytics (_ga, _gid)</li>
                      <li>• Page view tracking</li>
                      <li>• User journey analysis</li>
                      <li>• Performance monitoring</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-600 dark:border-purple-400 pl-4">
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">4. Marketing Cookies</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-2">
                      These cookies are used to track visitors across websites for advertising purposes.
                    </p>
                    <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-sm">
                      <li>• Advertising network cookies</li>
                      <li>• Retargeting pixels</li>
                      <li>• Social media tracking</li>
                      <li>• Conversion tracking</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Managing Cookies */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Managing Your Cookie Preferences</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  You can manage your cookie preferences at any time through our cookie consent banner or by clicking the 
                  "Cookie Settings" button in the footer of our website. You can choose to accept all cookies, only necessary 
                  cookies, or customize your preferences.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    <strong>Note:</strong> Disabling certain cookies may affect the functionality of our website and limit your 
                    ability to use some features.
                  </p>
                </div>
              </section>

              {/* Browser Settings */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Browser Cookie Controls</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  Most web browsers allow you to control cookies through their settings. You can typically:
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>View what cookies are stored and delete them individually</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>Block third-party cookies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>Block cookies from specific websites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>Block all cookies from being set</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>Delete all cookies when you close your browser</span>
                  </li>
                </ul>
              </section>

              {/* Third-Party Cookies */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Third-Party Cookies</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  We may use third-party services that set cookies on your device. These include:
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300 mt-4">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Google Analytics:</strong> For website analytics and performance monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Social Media Platforms:</strong> For social sharing and authentication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Payment Processors:</strong> For secure payment processing</span>
                  </li>
                </ul>
              </section>

              {/* Updates */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Updates to This Policy</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, 
                  legal, or regulatory reasons. We will notify you of any significant changes by posting the new policy on this 
                  page with an updated "Last updated" date.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Contact Us</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  If you have questions about our use of cookies, please contact us at:
                  <br />
                  <a href="mailto:Admin@Kaiventive.com" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">Admin@Kaiventive.com</a>
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Privacy</a>
              <a href="/terms" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicyPage;
